import { useCallback, useEffect, useState } from "react";
import "./bop030w010.css";
import BOP030W010Template from "./bop030w010.template";
import { useMaster, type MasterItem } from "../../common/MasterContext";
import { EXPENSE_TYPE, INCOME_TYPE, type Category } from "../../common/commonConstant";
import useApiRequest from "../../common/useApiRequest";
import { buildExpenseStatusKey, type ExpenseStatusEntry } from "../../common/expenseStatus";

// 汎用マスタの区分コード（hanyo_cd）。002=収支項目（収支区分・項目大・項目小の組）。
const HANYO_CD_SHUSHI_KOUMOKU = "000002";
const DESCRIPTION_SHUSHI_KOUMOKU = "収支項目";

const MAX_TARGET_AMOUNT = 100_000_000;

export interface MasterFormValues {
  hanyoKoumokuCd: string | null; // nullなら新規追加
  type: Category;
  categoryLarge: string;
  categorySmall: string;
}

const emptyForm: MasterFormValues = {
  hanyoKoumokuCd: null,
  type: EXPENSE_TYPE,
  categoryLarge: "",
  categorySmall: "",
};

/**
 * 機能ID: BOP030W010
 * 機能名: 項目管理画面（汎用マスタCRUD）
 */
const BOP030W010 = () => {
  const { items, isLoading: isMasterLoading, refetch } = useMaster();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formValues, setFormValues] = useState<MasterFormValues>(emptyForm);
  const { isLoading: isSubmitting, execute } = useApiRequest();
  const [expenseStatusMap, setExpenseStatusMap] = useState<Map<string, ExpenseStatusEntry>>(new Map());
  const [targetDrafts, setTargetDrafts] = useState<Map<string, string>>(new Map());
  const { execute: fetchExpenseStatus } = useApiRequest<ExpenseStatusEntry[]>();
  const { isLoading: isTargetSaving, execute: executeTarget } = useApiRequest();

  const shushiKoumokuItems = items.filter(
    (item) => item.hanyoCd === HANYO_CD_SHUSHI_KOUMOKU,
  );

  const loadExpenseStatus = useCallback(async () => {
    const result = await fetchExpenseStatus(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs03001/status`,
    );
    if (!result.ok) {
      alert("目標限度額の取得に失敗しました。画面を再読み込みしてご確認ください。");
      return;
    }
    if (result.data) {
      setExpenseStatusMap(
        new Map(
          result.data.map((s) => [
            buildExpenseStatusKey(s.type, s.categoryLarge, s.categorySmall),
            s,
          ]),
        ),
      );
    }
  }, [fetchExpenseStatus]);

  useEffect(() => {
    loadExpenseStatus();
  }, [loadExpenseStatus]);

  const handleToggleExpand = (categoryLarge: string) => {
    const next = new Set(expandedItems);
    if (next.has(categoryLarge)) {
      next.delete(categoryLarge);
    } else {
      next.add(categoryLarge);
    }
    setExpandedItems(next);
  };

  const handleBulkExpand = (keys: string[], expand: boolean) => {
    const next = new Set(expandedItems);
    keys.forEach((key) => {
      if (expand) {
        next.add(key);
      } else {
        next.delete(key);
      }
    });
    setExpandedItems(next);
  };

  const handleOpenAddForm = () => {
    setFormValues(emptyForm);
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (item: MasterItem) => {
    setFormValues({
      hanyoKoumokuCd: item.hanyoKoumokuCd,
      type: item.hanyoMoji1 === INCOME_TYPE ? INCOME_TYPE : EXPENSE_TYPE,
      categoryLarge: item.hanyoMoji2 || "",
      categorySmall: item.hanyoMoji3 || "",
    });
    setIsFormOpen(true);
  };

  const handleCancelForm = () => {
    setIsFormOpen(false);
    setFormValues(emptyForm);
  };

  const refetchAfterMutation = async () => {
    const refetchOk = await refetch();
    if (!refetchOk) {
      alert("一覧の再取得に失敗しました。画面を再読み込みしてご確認ください。");
    }
    await loadExpenseStatus();
  };

  const handleFormSubmit = async () => {
    const missingFields: string[] = [];
    if (!formValues.type) missingFields.push("収支");
    if (!formValues.categoryLarge.trim()) missingFields.push("項目（大）");
    if (!formValues.categorySmall.trim()) missingFields.push("項目（小）");

    if (missingFields.length > 0) {
      alert(`必須項目を入力してください: ${missingFields.join("、")}`);
      return;
    }

    const isEditing = formValues.hanyoKoumokuCd !== null;
    const url = isEditing
      ? `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs01002`
      : `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs01001`;

    const payload = {
      hanyoCd: HANYO_CD_SHUSHI_KOUMOKU,
      ...(isEditing ? { hanyoKoumokuCd: formValues.hanyoKoumokuCd } : {}),
      description: DESCRIPTION_SHUSHI_KOUMOKU,
      hanyoMoji1: formValues.type,
      hanyoMoji2: formValues.categoryLarge.trim(),
      hanyoMoji3: formValues.categorySmall.trim(),
    };

    const result = await execute(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (!result.ok) {
      alert(result.error.message);
      return;
    }

    alert(isEditing ? "更新成功" : "登録成功");
    setIsFormOpen(false);
    setFormValues(emptyForm);
    await refetchAfterMutation();
  };

  const handleDelete = async (item: MasterItem) => {
    const confirmed = window.confirm(
      `「${item.hanyoMoji2} / ${item.hanyoMoji3}」を削除してもよろしいですか？`,
    );
    if (!confirmed) return;

    const result = await execute(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs01003`,
      {
        method: "POST",
        body: JSON.stringify({
          hanyoCd: item.hanyoCd,
          hanyoKoumokuCd: item.hanyoKoumokuCd,
        }),
      },
    );

    if (!result.ok) {
      alert(result.error.message);
      return;
    }

    alert("削除成功");
    await refetchAfterMutation();
  };

  const handleTargetInputChange = (key: string, value: string) => {
    setTargetDrafts((prev) => {
      const next = new Map(prev);
      next.set(key, value);
      return next;
    });
  };

  const handleSaveTarget = async (item: MasterItem, key: string) => {
    const rawValue = targetDrafts.get(key) ?? "";
    const trimmed = rawValue.trim();
    const type = item.hanyoMoji1 || "";
    const categoryLarge = item.hanyoMoji2 || "";
    const categorySmall = item.hanyoMoji3 || "";

    const clearDraft = () => {
      setTargetDrafts((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    };

    if (trimmed === "") {
      const result = await executeTarget(
        `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs03003`,
        {
          method: "POST",
          body: JSON.stringify({ type, categoryLarge, categorySmall }),
        },
      );
      if (!result.ok) {
        alert(result.error.message);
        return;
      }
      clearDraft();
      await loadExpenseStatus();
      return;
    }

    const amount = Number(trimmed);
    if (!Number.isFinite(amount) || !Number.isInteger(amount) || amount < 0 || amount > MAX_TARGET_AMOUNT) {
      alert(`目標限度額は0〜${MAX_TARGET_AMOUNT}の整数で入力してください`);
      return;
    }

    const result = await executeTarget(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs03002`,
      {
        method: "POST",
        body: JSON.stringify({ type, categoryLarge, categorySmall, targetAmount: amount }),
      },
    );
    if (!result.ok) {
      alert(result.error.message);
      return;
    }
    clearDraft();
    await loadExpenseStatus();
  };

  const handleToggleDisplay = async (item: MasterItem, checked: boolean) => {
    const result = await executeTarget(
      `${process.env.REACT_APP_API_BASE_URL}/api/bop030rs03004`,
      {
        method: "POST",
        body: JSON.stringify({
          type: item.hanyoMoji1 || "",
          categoryLarge: item.hanyoMoji2 || "",
          categorySmall: item.hanyoMoji3 || "",
          displayFlg: checked ? 1 : 0,
        }),
      },
    );
    if (!result.ok) {
      alert(result.error.message);
      return;
    }
    await loadExpenseStatus();
  };

  const displayedCount = Array.from(expenseStatusMap.values()).filter(
    (s) => s.displayFlg === 1,
  ).length;

  return (
    <BOP030W010Template
      items={shushiKoumokuItems}
      expandedItems={expandedItems}
      isFormOpen={isFormOpen}
      formValues={formValues}
      isLoading={isMasterLoading || isSubmitting}
      expenseStatusMap={expenseStatusMap}
      targetDrafts={targetDrafts}
      isTargetSaving={isTargetSaving}
      displayedCount={displayedCount}
      onToggleExpand={handleToggleExpand}
      onBulkExpand={handleBulkExpand}
      onTargetInputChange={handleTargetInputChange}
      onOpenAddForm={handleOpenAddForm}
      onOpenEditForm={handleOpenEditForm}
      onCancelForm={handleCancelForm}
      onFormTypeChange={(type) => setFormValues((prev) => ({ ...prev, type }))}
      onFormCategoryLargeChange={(value) =>
        setFormValues((prev) => ({ ...prev, categoryLarge: value }))
      }
      onFormCategorySmallChange={(value) =>
        setFormValues((prev) => ({ ...prev, categorySmall: value }))
      }
      onFormSubmit={handleFormSubmit}
      onDelete={handleDelete}
      onSaveTarget={handleSaveTarget}
      onToggleDisplay={handleToggleDisplay}
    />
  );
};

export default BOP030W010;
